import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ── Color palette ──────────────────────────────────────────────────
const C = {
  primary:       [99, 102, 241],   // indigo accent
  headerBg:      [30, 35, 41],     // dark header
  headerText:    [234, 236, 239],  // light header text
  rowBg:         [255, 255, 255],
  altRowBg:      [248, 250, 252],
  cellText:       [30, 35, 41],
  mutedText:      [148, 163, 184],
  border:         [226, 232, 240],
  pageLine:       [203, 213, 225],
};

const DEFAULTS = {
  orientation:     'portrait',
  fontSize:        8,
  titleFontSize:   15,
  subtitleFontSize: 9,
  metaFontSize:    7,
  padding:         4,
  pageMargin:      14,
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  }).format(d);

const parseAlign = (col) => {
  if (col.align === 'right' || col.align === 'center') return col.align;
  return 'left';
};

// ══════════════════════════════════════════════════════════════════
//  PDF  EXPORT
// ══════════════════════════════════════════════════════════════════
export function exportToPDF(data, columns, options = {}) {
  const {
    title,
    subtitle,
    filename = 'export',
    orientation = DEFAULTS.orientation,
    fontSize = DEFAULTS.fontSize,
    titleFontSize = DEFAULTS.titleFontSize,
    subtitleFontSize = DEFAULTS.subtitleFontSize,
    metaFontSize = DEFAULTS.metaFontSize,
    headerBg = C.headerBg,
    headerText = C.headerText,
    altRowBg = C.altRowBg,
    cellText = C.cellText,
    borderColor = C.border,
    padding = DEFAULTS.padding,
    pageMargin = DEFAULTS.pageMargin,
  } = options;

  const doc = new jsPDF({ orientation, unit: 'mm' });
  const pageW = doc.internal.pageSize.width;
  const pageH = doc.internal.pageSize.height;

  // ── Build rows ──────────────────────────────────────────────────
  const headers = columns.map((c) => c.header);
  const rows = data.map((row) =>
    columns.map((col) => {
      const val = row[col.key];
      if (col.format) return col.format(val, row);
      if (val === null || val === undefined) return '';
      return String(val);
    })
  );

  // ── Build column styles from column.align ───────────────────────
  const columnStyles = {};
  columns.forEach((col, idx) => {
    const a = parseAlign(col);
    if (a !== 'left') columnStyles[idx] = { halign: a };
  });

  // ── Header block ────────────────────────────────────────────────
  let y = pageMargin + 2;

  // Accent bar
  doc.setFillColor(...C.primary);
  doc.rect(pageMargin, y - 2, 2.5, 8, 'F');

  if (title) {
    doc.setFontSize(titleFontSize);
    doc.setTextColor(...C.cellText);
    doc.setFont(undefined, 'bold');
    doc.text(String(title), pageMargin + 4.5, y + 2);
    y += titleFontSize * 0.4 + 3;
  }

  if (subtitle) {
    doc.setFontSize(subtitleFontSize);
    doc.setTextColor(...C.mutedText);
    doc.setFont(undefined, 'normal');
    doc.text(String(subtitle), pageMargin + 4.5, y + 1);
    y += subtitleFontSize * 0.4 + 3;
  }

  // Meta line — record count + generated timestamp
  doc.setFontSize(metaFontSize);
  doc.setTextColor(...C.mutedText);
  const meta = `${data.length} record${data.length !== 1 ? 's' : ''} · Generated ${formatDate(new Date())}`;
  doc.text(meta, pageMargin + 4.5, y + 0.5);
  y += metaFontSize * 0.5 + 4;

  // Thin separator line
  doc.setDrawColor(...C.pageLine);
  doc.setLineWidth(0.1);
  doc.line(pageMargin, y - 1, pageW - pageMargin, y - 1);

  const tableStartY = y + 1;

  // ── Auto-table ──────────────────────────────────────────────────
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: tableStartY,
    margin: { top: pageMargin, right: pageMargin, bottom: 16, left: pageMargin },
    styles: {
      fontSize,
      cellPadding: padding,
      textColor: cellText,
      lineColor: borderColor,
      lineWidth: 0.05,
    },
    headStyles: {
      fillColor: headerBg,
      textColor: headerText,
      fontStyle: 'bold',
      halign: 'left',
      valign: 'middle',
      minCellHeight: 7,
    },
    bodyStyles: {
      textColor: cellText,
      valign: 'middle',
      halign: 'left',
      minCellHeight: 5.5,
    },
    alternateRowStyles: {
      fillColor: altRowBg,
    },
    columnStyles,

    // ── Page header (repeated on overflow pages) ──────────────────
    didDrawPage: (hookData) => {
      const { pageNumber, pageCount } = doc.internal.getNumberOfPages
        ? { pageNumber: doc.internal.getCurrentPageInfo().pageNumber, pageCount: doc.internal.getNumberOfPages() }
        : { pageNumber: hookData.pageNumber, pageCount: hookData.pageCount };

      // Header band on overflow pages
      if (pageNumber > 1) {
        doc.setFontSize(metaFontSize);
        doc.setTextColor(...C.mutedText);
        doc.text(String(title || ''), pageMargin, pageMargin - 1);
        doc.setDrawColor(...C.pageLine);
        doc.setLineWidth(0.05);
        doc.line(pageMargin, pageMargin + 1, pageW - pageMargin, pageMargin + 1);
      }

      // Footer — page number (right) + timestamp (left)
      doc.setFontSize(metaFontSize);
      doc.setTextColor(...C.mutedText);

      // Left: export timestamp
      const ts = formatDate(new Date());
      doc.text(ts, pageMargin, pageH - 4);

      // Right: page number
      const pageStr = `Page ${pageNumber} / ${pageCount}`;
      const pageStrW = doc.getTextWidth(pageStr);
      doc.text(pageStr, pageW - pageMargin - pageStrW, pageH - 4);

      // Thin footer line
      doc.setDrawColor(...C.pageLine);
      doc.setLineWidth(0.05);
      doc.line(pageMargin, pageH - 6, pageW - pageMargin, pageH - 6);
    },
  });

  doc.save(`${filename}.pdf`);
}

// ══════════════════════════════════════════════════════════════════
//  EXCEL  EXPORT
// ══════════════════════════════════════════════════════════════════
export function exportToXLSX(data, columns, options = {}) {
  const { filename = 'export', sheetName = 'Sheet1' } = options;

  const headers = columns.map((col) => col.header);
  const rows = data.map((row) =>
    columns.map((col) => {
      const val = row[col.key];
      if (col.format) return col.format(val, row);
      return val ?? '';
    })
  );

  const csvData = headers.length > 0 ? [headers, ...rows] : rows;
  const worksheet = XLSX.utils.aoa_to_sheet(csvData);

  if (headers.length > 0) {
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    // Header styling
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (worksheet[addr]) {
        worksheet[addr].s = {
          font: { bold: true, color: { rgb: 'EAECEF' }, sz: 11 },
          fill: { fgColor: { rgb: '1E2329' }, patternType: 'solid' },
          alignment: { horizontal: columns[C]?.align === 'right' ? 'right' : 'left', vertical: 'center' },
        };
      }
    }
    // Column widths
    worksheet['!cols'] = columns.map((col) => ({
      wch: Math.max(col.header.length + 4, col.width || 12),
    }));
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${filename}.xlsx`);
}

// ══════════════════════════════════════════════════════════════════
export function exportData(data, columns, options = {}) {
  const { format = 'pdf' } = options;
  if (format === 'xlsx') {
    exportToXLSX(data, columns, options);
  } else {
    exportToPDF(data, columns, options);
  }
}
